/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  useTourTimeOptions
 */

import { selectNow } from '@zcode-ui/web-zumper-global-state'
import type { IntegratedTourType } from '@zcode/enums'
import type { Listable, MaybeEmptyProperty, Property } from '@zcode/state-type'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHasInstantTourBooking } from '../../TourOptions/useHasInstantTourBooking'
import { getDefaultIntervalChoices, getIntervalChoicesFromOfficeHours } from '../helpers'
import { useGetTourAvailability } from './useGetTourAvailability'

export const useTourTimeOptions = (
  entity: Listable | Property | MaybeEmptyProperty,
  type: IntegratedTourType,
  skip?: boolean
) => {
  const { fetching, providedTimesInterval } = useGetTourAvailability(entity, type, { skip })
  const { hasInstantTourAvailability } = useHasInstantTourBooking(entity, skip)
  const now = useSelector(selectNow)
  return useMemo(() => {
    // If a property has NO instant-tour availability (for any type), then we should fallback
    // to officeHours/default
    // If a property has SOME instant-tour availability (for any type), then we should instead
    // return the instant-tour availability (even if that's no availability for THIS `type`)
    if (fetching || providedTimesInterval?.size || hasInstantTourAvailability) {
      return { fetching, tourTimeOptionsByDate: providedTimesInterval }
    }
    const today = new Date(now)
    return {
      fetching: false,
      tourTimeOptionsByDate:
        (entity.hours?.length &&
          getIntervalChoicesFromOfficeHours({
            currentDateObj: today,
            hours: entity.hours,
            interval: 30,
            tz: entity.tz,
          })) ||
        getDefaultIntervalChoices({ interval: 30, dateObj: today, tz: entity.tz }),
    }
  }, [entity.hours, entity.tz, fetching, hasInstantTourAvailability, now, providedTimesInterval])
}
